import { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, CircularProgress, Alert, Box, Grid, TextField,
    MenuItem, Select, Autocomplete, Typography, IconButton, Chip, Divider, Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SaveIcon from '@mui/icons-material/Save';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
    createSuccessRequest, updateSuccessRequest, currentUserName,
    getRequestFiles, uploadRequestFiles, downloadRequestFile, deleteRequestFile,
    getRequestNotes, createRequestNote, updateRequestNote,
} from '../../../services/successRequestService';
import { getStaffDirectory } from '../../../services/workService';
import { normaliseRequestNote } from '../../../services/noteShapes';
import EditableNote from '../../Work/EditableNote';
import { RichTextEditor, RichTextView } from './RichText';
import Celebration from './Celebration';
import {
    REQUEST_STATUSES, REQUEST_PRIORITIES, REQUEST_TYPES,
    STATUS_CHIP_COLORS, PRIORITY_CHIP_COLORS,
} from './constants';
import { usePeopleNames } from '../../../services/peopleNames';
import ClientOwnerField from '../../Work/ClientOwnerField';
import { useClientOwner, setClientOwner } from '../../../services/clientOwners';

const BLUE = '#004df2';
const GREEN = '#2e7d32';
const emptyForm = {
    title: '', request_type: 'Feature', priority: 'Medium', status: 'Open',
    nexus_client_id: null, description: '', details: '', requested_by: '',
    assigned_to: '', resolution: '', due_on: '', stakeholders: [],
};

// Full-record UPDATE, not a partial patch (see Integrations' update_success_request) --
// every field must round-trip through form/buildPayload or a quick edit silently
// wipes whatever it doesn't carry, same trap due_on/stakeholders would fall into
// if either save path forgot them.
const dateOnly = (val) => (val && !String(val).startsWith('1900-01-01') ? String(val).slice(0, 10) : '');

// Request updates, newest first.
const sortNotes = (rows) => (Array.isArray(rows) ? rows : rows?.notes || [])
    .slice().sort((a, b) => String(b.CreatedOn || '').localeCompare(String(a.CreatedOn || '')));

const formatDateTime = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const ColorChip = ({ value, palette }) => {
    if (!value) return null;
    const c = palette[value];
    return (
        <Chip label={value} size="small"
            sx={{ backgroundColor: c?.bg, color: c?.text, fontWeight: 600, fontSize: 12, height: 24 }} />
    );
};

const Field = ({ label, children }) => (
    <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-secondary)', mb: 0.5 }}>
            {label}
        </Typography>
        {children}
    </Box>
);

const RequestFormDialog = ({ open, onClose, request, clients = [], onSaved }) => {
    const nameOf = usePeopleNames();
    const isEdit = Boolean(request?.NexusSuccessRequest_Id);
    const theme = useTheme();
    const paletteMode = theme.palette.mode === 'dark' ? 'dark' : 'light';
    const statusColors = STATUS_CHIP_COLORS[paletteMode];
    const priorityColors = PRIORITY_CHIP_COLORS[paletteMode];

    const [mode, setMode] = useState('edit'); // 'view' | 'edit'
    // Local, self-consistent copy of the record so quick-edits reflect
    // immediately without depending on the parent refreshing the `request` prop.
    const [data, setData] = useState(request || {});
    const [form, setForm] = useState(emptyForm);
    const [attachments, setAttachments] = useState([]);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [saving, setSaving] = useState(false);
    const [quickSaving, setQuickSaving] = useState(false);
    const [busyFile, setBusyFile] = useState(null);
    const [error, setError] = useState(null);
    const [celebrate, setCelebrate] = useState(false);
    // Internal staff, for the Requested by / Assigned to / Stakeholders pickers
    // below -- freeSolo, so an external contact's email still works, but a
    // real Nexus person's email is one click instead of typed by hand.
    const [staff, setStaff] = useState([]);
    useEffect(() => { getStaffDirectory().then(setStaff); }, []);
    // Client owner (district), #4320: kept in the Analytics item-metadata
    // ledger, not on the request row, so it is NOT part of `form` (which is
    // the Integrations API's body).
    const district = useClientOwner({ source: 'request', id: request?.NexusSuccessRequest_Id || '' });
    const [clientOwnerDraft, setClientOwnerDraft] = useState('');
    const staffEmails = staff.map((s) => s.email);

    const descRef = useRef(null);
    const detailsRef = useRef(null);
    const resolutionRef = useRef(null); // edit-mode resolution
    const quickResRef = useRef(null);   // view-mode quick resolution

    // Updates: success.NexusSuccessRequestNote, the same notes a work row's
    // Update action posts. Until 2026-09-23 nothing here showed them, so an
    // update written from the work views was invisible on the request itself.
    const [notes, setNotes] = useState(null);
    // An unreadable update log is said, never shown as "No updates yet".
    const [notesError, setNotesError] = useState(null);
    const [noteDraft, setNoteDraft] = useState('');
    const [postingNote, setPostingNote] = useState(false);
    const loadNotes = useCallback((id) => {
        setNotes(null);
        setNotesError(null);
        getRequestNotes(id)
            .then((rows) => setNotes(sortNotes(rows)))
            .catch((e) => setNotesError(e.message || 'unknown error'));
    }, []);
    // Edit one of your own updates (ADO #4318); the server checks authorship.
    const editNote = async (n, text) => {
        setNotes(sortNotes(await updateRequestNote(data.NexusSuccessRequest_Id, n.NexusSuccessRequestNote_Id, text)));
    };
    const postNote = async () => {
        const text = noteDraft.trim();
        if (!text || !data?.NexusSuccessRequest_Id) return;
        setPostingNote(true);
        setError(null);
        try {
            await createRequestNote(data.NexusSuccessRequest_Id, text, await currentUserName());
            setNoteDraft('');
            loadNotes(data.NexusSuccessRequest_Id);
        } catch (err) { setError(err.message); }
        finally { setPostingNote(false); }
    };

    const prefill = useCallback((src) => {
        if (isEdit && src) {
            setForm({
                title: src.Title || '',
                request_type: src.RequestType || 'Feature',
                priority: src.Priority || 'Medium',
                status: src.Status || 'Open',
                nexus_client_id: src.NexusClient_Id || null,
                description: src.Description || '',
                details: src.Details || '',
                requested_by: src.RequestedBy || '',
                assigned_to: src.AssignedTo || '',
                resolution: src.Resolution || '',
                due_on: dateOnly(src.DueDate),
                stakeholders: src.Stakeholders || [],
            });
        } else {
            setForm(emptyForm);
        }
    }, [isEdit]);

    // Reset / prefill each time the dialog opens. Existing records open in view
    // mode; new records go straight to the edit form.
    useEffect(() => {
        if (!open) return;
        setError(null);
        setPendingFiles([]);
        setCelebrate(false);
        setData(request || {});
        prefill(request);
        setMode(isEdit ? 'view' : 'edit');
        setNoteDraft('');
        setClientOwnerDraft(district.value);
        if (isEdit) {
            getRequestFiles(request.NexusSuccessRequest_Id).then(setAttachments).catch(() => setAttachments([]));
            loadNotes(request.NexusSuccessRequest_Id);
        } else {
            setAttachments([]);
        }
    }, [open, isEdit, request, prefill, loadNotes]); // eslint-disable-line react-hooks/exhaustive-deps

    const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const selectedClient = clients.find((c) => c.clientId === form.nexus_client_id) || null;
    const clientName = data?.ClientName || selectedClient?.name || '';

    const handleClose = () => { if (!saving && !quickSaving) onClose(); };

    const handleCancelEdit = () => {
        if (isEdit) { prefill(data); setError(null); setPendingFiles([]); setMode('view'); }
        else onClose();
    };

    // --- Quick edit (view mode): persist status/resolution without full edit ---
    const buildPayload = (statusVal, resolutionVal) => ({
        title: data.Title,
        request_type: data.RequestType,
        priority: data.Priority,
        status: statusVal,
        nexus_client_id: data.NexusClient_Id,
        description: data.Description,
        details: data.Details,
        requested_by: data.RequestedBy,
        assigned_to: data.AssignedTo,
        resolution: resolutionVal,
        due_on: dateOnly(data.DueDate) || null,
        stakeholders: data.Stakeholders || [],
    });

    const quickSave = async (statusVal, resolutionVal) => {
        setQuickSaving(true);
        setError(null);
        const prev = data;
        try {
            const who = await currentUserName();
            const updated = await updateSuccessRequest(
                data.NexusSuccessRequest_Id,
                { ...buildPayload(statusVal, resolutionVal), updated_by: who },
            );
            setData(updated || { ...data, Status: statusVal, Resolution: resolutionVal });
            await onSaved?.();
            return true;
        } catch (err) {
            setData(prev);
            setError(err.message);
            return false;
        } finally {
            setQuickSaving(false);
        }
    };

    const currentResolutionHtml = () => quickResRef.current?.getHTML() ?? (data.Resolution || '');

    const handleQuickStatus = (e) => quickSave(e.target.value, currentResolutionHtml());
    const handleSaveNotes = () => quickSave(data.Status, currentResolutionHtml());
    const handleResolve = async () => {
        const ok = await quickSave('Completed', currentResolutionHtml());
        if (ok) {
            setCelebrate(true);
            setTimeout(() => setCelebrate(false), 1900);
        }
    };

    // --- Attachments ---
    const handleAddFiles = (e) => {
        const chosen = Array.from(e.target.files || []);
        if (chosen.length) setPendingFiles((prev) => [...prev, ...chosen]);
        e.target.value = '';
    };
    const removePending = (idx) => setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

    const handleDownload = async (filename) => {
        try { await downloadRequestFile(data.NexusSuccessRequest_Id, filename); }
        catch (err) { setError(err.message); }
    };

    const handleDeletePersisted = async (filename) => {
        setBusyFile(filename);
        setError(null);
        try {
            const who = await currentUserName();
            setAttachments(await deleteRequestFile(data.NexusSuccessRequest_Id, filename, who));
        } catch (err) { setError(err.message); }
        finally { setBusyFile(null); }
    };

    // --- Full edit save ---
    const handleSave = async () => {
        if (!form.title.trim()) { setError('Title is required.'); return; }
        if (!form.nexus_client_id) { setError('A client is required.'); return; }
        setSaving(true);
        setError(null);
        try {
            const who = await currentUserName();
            const payload = {
                ...form,
                // blank must go as null: '' is stored as 1900-01-01
                due_on: form.due_on || null,
                description: descRef.current?.getHTML() ?? form.description,
                details: detailsRef.current?.getHTML() ?? form.details,
                resolution: resolutionRef.current?.getHTML() ?? form.resolution,
            };
            let savedId;
            if (isEdit) {
                await updateSuccessRequest(data.NexusSuccessRequest_Id, { ...payload, updated_by: who });
                savedId = data.NexusSuccessRequest_Id;
            } else {
                const created = await createSuccessRequest({
                    ...payload,
                    requested_by: payload.requested_by || who,
                    created_by: who,
                });
                savedId = created?.NexusSuccessRequest_Id;
            }
            if (pendingFiles.length && savedId) await uploadRequestFiles(savedId, pendingFiles, who);
            // Only when it changed, and on an existing request only when it
            // could be read (so a save can't blank it).
            const owner = clientOwnerDraft.trim();
            if (savedId && (!isEdit || district.status === 'ready') && owner !== (isEdit ? district.value : '')) {
                try {
                    await setClientOwner({ source: 'request', id: String(savedId), title: form.title,
                        clientId: form.nexus_client_id }, owner);
                } catch (err) {
                    await onSaved?.();
                    setError(`The request was saved, but its Client owner did not save (${err.message}).`);
                    return;
                }
            }
            await onSaved?.();
            onClose();
        } catch (err) { setError(err.message); }
        finally { setSaving(false); }
    };

    const enterEdit = () => {
        // carry any quick-edited status/resolution into the full form
        prefill({ ...data, Status: data.Status, Resolution: currentResolutionHtml() });
        setClientOwnerDraft(district.value);
        setError(null);
        setMode('edit');
    };

    const editorKey = `${data?.NexusSuccessRequest_Id || 'new'}-${mode}`;
    const isView = mode === 'view';
    const resolved = data?.Status === 'Completed';

    return (
        <>
            <Celebration show={celebrate} />
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: 'nexus-font, sans-serif', fontSize: 18 }}>
                    {isView ? (data?.Title || 'Request') : (isEdit ? 'Edit request' : 'New request')}
                </DialogTitle>

                <DialogContent dividers>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    {isView ? (
                        /* ---------- VIEW MODE (with quick edit) ---------- */
                        <Grid container spacing={2.5}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Select
                                        value={data?.Status || 'Open'}
                                        onChange={handleQuickStatus}
                                        size="small"
                                        disabled={quickSaving}
                                        sx={{ minWidth: 150, height: 32, fontSize: 13 }}
                                        renderValue={(v) => <ColorChip value={v} palette={statusColors} />}
                                    >
                                        {REQUEST_STATUSES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>)}
                                    </Select>
                                    <ColorChip value={data?.Priority} palette={priorityColors} />
                                    {data?.RequestType && <Chip label={data.RequestType} size="small" variant="outlined" sx={{ height: 24, fontSize: 12 }} />}
                                    {quickSaving && <CircularProgress size={16} />}
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Tooltip title={resolved ? 'Already resolved' : 'Mark completed'}>
                                        <span>
                                            <Button onClick={handleResolve} variant="contained" size="small"
                                                startIcon={<TaskAltIcon />} disabled={resolved || quickSaving}
                                                sx={{ textTransform: 'none', backgroundColor: GREEN, '&:hover': { backgroundColor: '#256a2b' } }}>
                                                {resolved ? 'Resolved' : 'Resolve'}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={4}><Field label="Client"><Typography sx={{ fontSize: 13 }}>{clientName || '—'}</Typography></Field></Grid>
                            <Grid item xs={12} sm={4}><Field label="Requested by"><Typography sx={{ fontSize: 13 }}>{nameOf(data?.RequestedBy) || '—'}</Typography></Field></Grid>
                            <Grid item xs={12} sm={4}><Field label="Assigned to"><Typography sx={{ fontSize: 13 }}>{nameOf(data?.AssignedTo) || '—'}</Typography></Field></Grid>
                            <Grid item xs={12} sm={4}><Field label="Deadline"><Typography sx={{ fontSize: 13 }}>{dateOnly(data?.DueDate) ? formatDateTime(data.DueDate) : '—'}</Typography></Field></Grid>
                            <Grid item xs={12} sm={8}>
                                <Field label="Stakeholders">
                                    {(data?.Stakeholders || []).length === 0
                                        ? <Typography sx={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>None</Typography>
                                        : (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {data.Stakeholders.map((s) => (
                                                    <Chip key={s} label={nameOf(s)} title={s} size="small" variant="outlined" sx={{ height: 22, fontSize: 12 }} />
                                                ))}
                                            </Box>
                                        )}
                                </Field>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Field label="Client owner (district)">
                                    <Typography sx={{ fontSize: 13, color: district.status === 'error' ? '#b45309' : undefined }}
                                        title="For the team's information only — Nexus never emails or nudges them">
                                        {district.status === 'error' ? 'couldn’t read'
                                            : district.status !== 'ready' ? '…' : (district.value || '—')}
                                    </Typography>
                                </Field>
                            </Grid>

                            <Grid item xs={12}><Field label="Description"><RichTextView value={data?.Description} /></Field></Grid>
                            <Grid item xs={12}><Field label="Details"><RichTextView value={data?.Details} /></Field></Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-secondary)' }}>
                                        Resolution notes
                                    </Typography>
                                    <Button onClick={handleSaveNotes} size="small" startIcon={quickSaving ? <CircularProgress size={14} /> : <SaveIcon sx={{ fontSize: 16 }} />}
                                        disabled={quickSaving} sx={{ textTransform: 'none', color: BLUE }}>
                                        Save notes
                                    </Button>
                                </Box>
                                <RichTextEditor ref={quickResRef} key={`vres-${data?.NexusSuccessRequest_Id}`}
                                    value={data?.Resolution} onChange={() => {}} disabled={quickSaving} />
                            </Grid>

                            <Grid item xs={12}>
                                <Field label="Attachments">
                                    {attachments.length === 0
                                        ? <Typography sx={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>None</Typography>
                                        : attachments.map((a) => (
                                            <Box key={a.filename} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
                                                <InsertDriveFileIcon sx={{ fontSize: 18, color: 'var(--color-text-secondary)' }} />
                                                <Typography sx={{ fontSize: 13, flexGrow: 1 }}>{a.filename}</Typography>
                                                <IconButton size="small" onClick={() => handleDownload(a.filename)}><DownloadIcon sx={{ fontSize: 18 }} /></IconButton>
                                            </Box>
                                        ))}
                                </Field>
                            </Grid>

                            <Grid item xs={12}>
                                <Field label={`Updates${notes && notes.length ? ` (${notes.length})` : ''}`}>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        <TextField size="small" fullWidth multiline maxRows={4}
                                            placeholder="Post an update (resets the update cadence)"
                                            value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
                                            inputProps={{ 'aria-label': 'Post an update' }} />
                                        <Button onClick={postNote} disabled={postingNote || !noteDraft.trim()}
                                            size="small" sx={{ textTransform: 'none', color: BLUE, whiteSpace: 'nowrap' }}>
                                            {postingNote ? <CircularProgress size={14} /> : 'Post update'}
                                        </Button>
                                    </Box>
                                    {notesError && (
                                        <Alert severity="warning" sx={{ fontSize: 12 }}>
                                            Couldn’t read the updates on this request ({notesError}). There may be updates you can’t see.
                                        </Alert>
                                    )}
                                    {notes === null && !notesError && <CircularProgress size={16} />}
                                    {notes && notes.length === 0 && (
                                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>No updates yet.</Typography>
                                    )}
                                    {(notes || []).map((n) => (
                                        <Box key={n.NexusSuccessRequestNote_Id || `${n.CreatedOn}-${n.Comment}`}
                                            sx={{ py: 0.75, borderTop: '1px solid var(--color-border, rgba(127,127,127,.2))' }}>
                                            <EditableNote note={normaliseRequestNote(n)} textSx={{ fontSize: 13 }}
                                                nameOf={nameOf} when={formatDateTime}
                                                onSave={(text) => editNote(n, text)}
                                                byline={(
                                                    <Typography sx={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                                                        {nameOf(n.CreatedBy) || '—'} · {formatDateTime(n.CreatedOn)}
                                                    </Typography>
                                                )} />
                                        </Box>
                                    ))}
                                </Field>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ mb: 1 }} />
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                                    Created by {nameOf(data?.CreatedBy) || '—'} on {formatDateTime(data?.CreatedOn)}
                                    {data?.UpdatedOn ? ` · Updated by ${nameOf(data?.UpdatedBy) || '—'} on ${formatDateTime(data?.UpdatedOn)}` : ''}
                                </Typography>
                            </Grid>
                        </Grid>
                    ) : (
                        /* ---------- EDIT MODE ---------- */
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField label="Title" value={form.title} onChange={setField('title')}
                                    fullWidth size="small" required autoFocus />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField select label="Type" value={form.request_type} onChange={setField('request_type')} fullWidth size="small">
                                    {REQUEST_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField select label="Priority" value={form.priority} onChange={setField('priority')} fullWidth size="small">
                                    {REQUEST_PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField select label="Status" value={form.status} onChange={setField('status')} fullWidth size="small">
                                    {REQUEST_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={clients}
                                    getOptionLabel={(o) => o.name || ''}
                                    value={selectedClient}
                                    onChange={(_, val) => setForm((f) => ({ ...f, nexus_client_id: val?.clientId ?? null }))}
                                    isOptionEqualToValue={(o, v) => o.clientId === v.clientId}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Client" size="small" required
                                            error={!form.nexus_client_id}
                                            helperText={!form.nexus_client_id ? 'Required' : ' '} />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Autocomplete
                                    freeSolo options={staffEmails}
                                    inputValue={form.requested_by}
                                    onInputChange={(_, val) => setForm((f) => ({ ...f, requested_by: val }))}
                                    renderOption={(props, option) => {
                                        const s = staff.find((x) => x.email === option);
                                        return <li {...props} key={option}>{s ? `${s.name} (${s.email})` : option}</li>;
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Requested by" size="small" fullWidth
                                            placeholder="Name or email" />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Autocomplete
                                    freeSolo options={staffEmails}
                                    inputValue={form.assigned_to}
                                    onInputChange={(_, val) => setForm((f) => ({ ...f, assigned_to: val }))}
                                    renderOption={(props, option) => {
                                        const s = staff.find((x) => x.email === option);
                                        return <li {...props} key={option}>{s ? `${s.name} (${s.email})` : option}</li>;
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Assigned to" size="small" fullWidth
                                            placeholder="Name or email" />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Deadline" type="date" value={form.due_on} onChange={setField('due_on')}
                                    fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <Autocomplete
                                    multiple freeSolo options={staffEmails}
                                    value={form.stakeholders}
                                    onChange={(_, val) => setForm((f) => ({ ...f, stakeholders: val }))}
                                    renderOption={(props, option) => {
                                        const s = staff.find((x) => x.email === option);
                                        return <li {...props} key={option}>{s ? `${s.name} (${s.email})` : option}</li>;
                                    }}
                                    renderTags={(val, getTagProps) => val.map((s, i) => (
                                        // eslint-disable-next-line react/jsx-key
                                        <Chip label={s} size="small" {...getTagProps({ index: i })} />
                                    ))}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Stakeholders" size="small"
                                            placeholder="Email, then Enter" />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ClientOwnerField clientId={form.nexus_client_id} value={clientOwnerDraft}
                                    onChange={setClientOwnerDraft} unreadable={isEdit && district.status === 'error'} />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>Description</Typography>
                                <RichTextEditor ref={descRef} key={`desc-${editorKey}`} value={form.description}
                                    onChange={(html) => setForm((f) => ({ ...f, description: html }))} disabled={saving} />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>Details</Typography>
                                <RichTextEditor ref={detailsRef} key={`details-${editorKey}`} value={form.details}
                                    onChange={(html) => setForm((f) => ({ ...f, details: html }))} disabled={saving} />
                            </Grid>
                            {isEdit && (
                                <Grid item xs={12}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>Resolution</Typography>
                                    <RichTextEditor ref={resolutionRef} key={`res-${editorKey}`} value={form.resolution}
                                        onChange={(html) => setForm((f) => ({ ...f, resolution: html }))} disabled={saving} />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Divider sx={{ mb: 1.5 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Attachments</Typography>
                                    <Button component="label" size="small" startIcon={<AttachFileIcon />} sx={{ textTransform: 'none', color: BLUE }}>
                                        Add files
                                        <input type="file" multiple hidden onChange={handleAddFiles} />
                                    </Button>
                                </Box>

                                {attachments.length === 0 && pendingFiles.length === 0 && (
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>No attachments yet.</Typography>
                                )}
                                {attachments.map((a) => (
                                    <Box key={a.filename} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                                        <InsertDriveFileIcon sx={{ fontSize: 18, color: 'var(--color-text-secondary)' }} />
                                        <Typography sx={{ fontSize: 13, flexGrow: 1 }}>{a.filename}</Typography>
                                        <IconButton size="small" onClick={() => handleDownload(a.filename)}><DownloadIcon sx={{ fontSize: 18 }} /></IconButton>
                                        <IconButton size="small" disabled={busyFile === a.filename} onClick={() => handleDeletePersisted(a.filename)}>
                                            {busyFile === a.filename ? <CircularProgress size={16} /> : <DeleteIcon sx={{ fontSize: 18, color: '#c62828' }} />}
                                        </IconButton>
                                    </Box>
                                ))}
                                {pendingFiles.map((f, idx) => (
                                    <Box key={`${f.name}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                                        <InsertDriveFileIcon sx={{ fontSize: 18, color: BLUE }} />
                                        <Typography sx={{ fontSize: 13, flexGrow: 1 }}>{f.name}</Typography>
                                        <Chip label="pending" size="small" sx={{ height: 20, fontSize: 11 }} />
                                        <IconButton size="small" onClick={() => removePending(idx)}><DeleteIcon sx={{ fontSize: 18, color: '#c62828' }} /></IconButton>
                                    </Box>
                                ))}
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 2, py: 1.5 }}>
                    {isView ? (
                        <>
                            <Button onClick={handleClose} color="inherit" size="small">Close</Button>
                            <Button onClick={enterEdit} variant="contained" size="small"
                                startIcon={<EditIcon />} sx={{ backgroundColor: BLUE, '&:hover': { backgroundColor: '#0040c8' } }}>
                                Edit
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={handleCancelEdit} color="inherit" size="small" disabled={saving}>Cancel</Button>
                            <Button onClick={handleSave} variant="contained" size="small" disabled={saving}
                                sx={{ backgroundColor: BLUE, '&:hover': { backgroundColor: '#0040c8' } }}>
                                {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : (isEdit ? 'Save' : 'Create')}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default RequestFormDialog;
